import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { getDailyFX } from "@/lib/dailyFX";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FX_CACHE_KEY = "fortune.fx.daily.currency-live";

const CurrencyContext = createContext({
  display: "USD",
  setDisplay: () => {},
  rates: {},
  format: (amt, from = "USD") => `$${(Number(amt) || 0).toLocaleString()}`,
});

const SYMBOLS = {
  USD: "$", INR: "₹", EUR: "€", GBP: "£", AED: "AED ", NPR: "Rs ",
  BTN: "Nu ", AUD: "A$", SGD: "S$", CNY: "¥", JPY: "¥", CAD: "C$",
  HKD: "HK$", THB: "฿", KRW: "₩", CHF: "CHF ",
};

export function CurrencyProvider({ children }) {
  const { user } = useAuth();
  const [display, setDisplayState] = useState(
    () => localStorage.getItem("fs_display_ccy") || user?.display_currency || "USD"
  );
  const [rates, setRates] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (user?.display_currency && user.display_currency !== display) {
      setDisplayState(user.display_currency);
      localStorage.setItem("fs_display_ccy", user.display_currency);
    }
  }, [user]); // eslint-disable-line

  // Fetch rates once per day (cached in localStorage)
  useEffect(() => {
    let alive = true;
    getDailyFX(`${API}/currency/live`, FX_CACHE_KEY).then((r) => {
      if (alive && r?.rates) setRates(r.rates);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Force-refresh — bypasses daily cache. Used by the portal currency picker.
  const refresh = async () => {
    setRefreshing(true);
    try {
      localStorage.removeItem(FX_CACHE_KEY);
      const r = await axios.get(`${API}/currency/live?refresh=1`);
      if (r.data?.rates) setRates(r.data.rates);
      localStorage.setItem(FX_CACHE_KEY, JSON.stringify({ ...r.data, cached_at: Date.now() }));
    } catch (err) {
      console.warn("[CurrencyContext] refresh failed:", err?.message || err);
    } finally {
      setRefreshing(false);
    }
  };

  const setDisplay = async (ccy) => {
    setDisplayState(ccy);
    localStorage.setItem("fs_display_ccy", ccy);
    if (user) {
      try {
        await axios.put(`${API}/currency/preference`,
          { display_currency: ccy }, { withCredentials: true });
      } catch (err) {
        console.warn("[CurrencyContext] save preference failed:", err?.message || err);
      }
    }
  };

  // amount in `from` currency → format as display currency using current spot
  const format = (amt, from = "USD", showCcy = true) => {
    const n = Number(amt) || 0;
    if (!rates[from] || !rates[display]) {
      const sym = SYMBOLS[from] || "$";
      return `${sym}${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    }
    const usd = n / rates[from];
    const out = usd * rates[display];
    const sym = SYMBOLS[display] || "$";
    return `${sym}${out.toLocaleString(undefined, { maximumFractionDigits: 2 })}${showCcy && display !== "USD" ? "" : ""}`;
  };

  return (
    <CurrencyContext.Provider value={{
      display, setDisplay, rates, format,
      refresh, refreshing,
      currencies: Object.keys(SYMBOLS),
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyContext);
